import {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    use,
    useCallback,
    useEffect,
    useState,
} from "react";

import { useMsal } from "@azure/msal-react";
import { AccountInfo, EventType } from "@azure/msal-browser";

import { createMyAccountApiAsync } from "@/services/api-service";
import { useError } from "./error";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { identify, Identify, setUserId } from "@amplitude/analytics-browser";
import { IlluminateUserPermissionsEnum } from "@/api/openapi";

const debug: boolean = false;

export interface IUserInfo {
    name: string;
    userType: string; //IlluminateUserUserTypeEnum
    username: string;
    organisationId?: string;
    externalUserId: string;
    userAccountId: number;

    accessTags: string[];
    permissions: IlluminateUserPermissionsEnum[]; //IlluminateUserPermissionsEnum
    deviceGroupTags: string[];
    roles: string[];

    platformSource: string;
    identityProvider: string;
    termsAcceptedAt?: Date;
}

interface IUserContext {
    setUser: (user: IUserInfo | undefined) => void;
    user?: IUserInfo;
    setIsLoading: (nextIsLoading: boolean) => void;
    isLoading: boolean;
    setIsFetching: (nextIsFetching: boolean) => void;
    isFetching: boolean;
    error?: Error;
    isError: boolean;
    refetch: () => void;
}

export const UserContext = createContext<IUserContext>({
    setUser: (user: IUserInfo | undefined) => {
        console.warn(
            "No context setUser handler has been set.\r\nUser Info: ",
            user,
        );
    },
    user: undefined,
    setIsLoading: () => {
        console.warn("No context setIsLoading handler has been set.");
    },
    isLoading: false,
    setIsFetching: () => {
        console.warn("No context setIsLoading handler has been set.");
    },
    isFetching: false,
    error: undefined,
    isError: false,
    refetch: () => {
        console.warn("No context refetch handler has been set.");
    },
});

export const useUser = () => use(UserContext);

/**
 * User context provider.
 *
 * @param options
 * @returns
 */
export const UserProvider: FunctionComponent<PropsWithChildren> = (
    options,
): ReactElement => {
    const [user, setUser] = useState<IUserInfo | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [subscribed, setSubscribed] = useState<boolean>(false);
    const { instance } = useMsal();
    const errorContext = useError();
    const ldClient = useLDClient();

    const fetchAndSetUser = useCallback(async () => {
        const equals = (objA?: IUserInfo, objB?: IUserInfo): boolean => {
            return JSON.stringify(objA) === JSON.stringify(objB);
        };
        setIsFetching(true);
        try {
            const api = await createMyAccountApiAsync(errorContext, instance);
            const dto = await api.getMyAccountInfo();

            const newUser: IUserInfo = {
                name: dto.name || "",
                userType: dto.userType || "",
                username: dto.username || "",
                organisationId: dto.organisationId,
                externalUserId: dto.externalUserId || "",
                userAccountId: dto.userAccountId || 0,

                accessTags: [],
                deviceGroupTags: [],
                permissions: dto.permissions || [],
                roles: dto.roles || [],

                platformSource: dto.platformSource || "",
                identityProvider: dto.identityProvider || "",
                termsAcceptedAt: dto.termsAcceptedAt,
            };
            if (dto.organisationId) newUser.accessTags.push(dto.organisationId);
            if (dto.organisationGroups)
                newUser.accessTags.push(...dto.organisationGroups);
            if (dto.deviceGroupTags)
                newUser.deviceGroupTags.push(...dto.deviceGroupTags);

            const paddedUserAccountId = String(newUser.userAccountId).padStart(
                5,
                "0",
            );

            setUserId(paddedUserAccountId);

            const identifyEvent = new Identify();
            identifyEvent.set("Organisation", newUser.organisationId ?? "none");
            identifyEvent.set(
                "LoginType",
                localStorage.getItem("msalDirectory") ?? "adb2c",
            );
            identifyEvent.set("Username", newUser.username);

            identify(identifyEvent);

            if (newUser && ldClient) {
                // Update the LaunchDarkly user context
                await ldClient.identify({
                    kind: "multi",
                    user: {
                        key: paddedUserAccountId,
                        name: newUser.name,
                        custom: {
                            userType: newUser.userType,
                            username: newUser.username,
                            organisationId: newUser.organisationId,
                            externalUserId: newUser.externalUserId,
                            loginType:
                                localStorage.getItem("msalDirectory") ??
                                "adb2c",
                        },
                    },
                    organisation: { key: newUser.organisationId },
                });
            }

            if (!equals(user, newUser)) {
                if (debug) {
                    console.debug("User context change detected.", newUser);
                }
                setUser(newUser);
            }
            setError(undefined);
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            setIsLoading(false);
            setIsFetching(false);
        }
    }, [errorContext, instance, ldClient, user]);

    useEffect(() => {
        if (subscribed) {
            return;
        }

        setSubscribed(true);

        const account: AccountInfo | null = instance.getActiveAccount(); //  Property 'account' does not exist on type 'PopupEvent'.ts(2339)

        if (account) {
            fetchAndSetUser();
        }

        instance.addEventCallback((message) => {
            if (message.eventType === EventType.LOGIN_SUCCESS) {
                if (message.payload) {
                    fetchAndSetUser();
                } else {
                    setUser(undefined);
                }
            }
        });
    }, [fetchAndSetUser, instance, subscribed]);

    return (
        <UserContext
            value={{
                user,
                setUser,
                isLoading,
                setIsLoading,
                isFetching,
                setIsFetching,
                error,
                isError: !!error,
                refetch: fetchAndSetUser,
            }}
        >
            {options.children}
        </UserContext>
    );
};

export const MockUserProvider = (props: {
    user?: IUserInfo | undefined;
    isLoading?: boolean;
    isFetching?: boolean;
    error?: Error;
    children?: ReactNode;
}) => {
    const [user, setUser] = useState(props.user);
    const [isLoading, setIsLoading] = useState(props.isLoading ?? false);
    const [isFetching, setIsFetching] = useState(props.isFetching ?? false);
    const [error] = useState(props.error);

    return (
        <UserContext
            value={{
                user,
                setUser,
                isLoading,
                setIsLoading,
                isFetching,
                setIsFetching,
                error,
                isError: !!error,
                refetch: () => null,
            }}
        >
            {props.children}
        </UserContext>
    );
};
